import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { Job, Van } from "./api"
import { useProducao } from "./context"
import { ContractNotices, MaterialBadge, VanPill } from "./components"
import {
  addDays,
  fmtDay,
  fmtShort,
  initialsOf,
  isAbsent,
  jobDays,
  kindText,
  mondayOf,
  teamOf,
  todayIso,
  weekDays,
  isHeld,
  holdReasonOf,
} from "./lib"

const OCCUPYING = new Set(["planeada", "execucao", "parada", "concluida"])

/**
 * G2 · Semana — só de leitura nesta fase (arrastar e reagendar fica para a
 * Fase 4). Uma linha por carrinha + «Sem carrinha», segunda a sábado. Obras
 * de vários dias ocupam várias células.
 */
export function Semana() {
  const { board, loading, error, openAssign, refreshContracts } = useProducao()
  const navigate = useNavigate()
  // Modo `host`: ao abrir a semana, o estado (parada/cancelada) vem do contrato.
  useEffect(() => refreshContracts(), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [monday, setMonday] = useState(mondayOf(todayIso()))
  const days = weekDays(monday)
  const today = todayIso()

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" /></div>
  if (error || !board) return <p className="py-24 text-center text-sm text-destructive">{error ?? "Sem dados."}</p>

  const threshold = board.config.overload_threshold
  const planned = board.jobs.filter((j) => OCCUPYING.has(j.status) && j.scheduled_date)
  const rows: Array<{ van: Van | null }> = [
    ...board.vans.filter((v) => v.active).map((van) => ({ van })),
    { van: null },
  ]
  const porAtribuir = board.jobs.filter((j) => j.status === "por_atribuir")
  const weekAbsences = board.absences.filter((a) => a.date_to >= days[0]! && a.date_from <= days[5]!)

  const cellJobs = (vanId: string | null, day: string) =>
    planned.filter((j) => (j.van_id ?? null) === vanId && jobDays(j).includes(day))

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="min-w-0 flex-1 space-y-3">
        <ContractNotices board={board} />
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">
            Semana de {fmtShort(days[0]!)} a {fmtShort(days[5]!)}
          </h1>
          <div className="flex items-center gap-1 sm:ml-2">
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setMonday(addDays(monday, -7))} aria-label="Semana anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMonday(mondayOf(today))}>Hoje</Button>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setMonday(addDays(monday, 7))} aria-label="Semana seguinte">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <p className="w-full text-xs text-muted-foreground md:ml-auto md:w-auto">
            Média de 2 a 3 obras por carrinha por dia · aviso a partir da {threshold}.ª
          </p>
        </div>

        {/* Telemóvel: um dia de cada vez, com a carrinha em cada obra. */}
        <div className="space-y-3 md:hidden">
          {days.map((d) => {
            const js = planned.filter((j) => jobDays(j).includes(d))
            const overVans = board.vans.filter((v) => js.filter((j) => j.van_id === v.id).length >= threshold)
            return (
              <section key={d} className={cn("rounded-xl border p-3 border-border", d === today && "border-accent/40 bg-accent/5")}>
                <h2 className="mb-2 text-sm font-semibold">
                  {fmtDay(d)}
                  {d === today && <span className="ml-1 font-normal text-accent">· hoje</span>}
                </h2>
                {js.length > 0 ? (
                  <ul className="space-y-2">
                    {js.map((j) => (
                      <li key={j.id}>
                        <WeekCard job={j} day={d} van={board.vans.find((v) => v.id === j.van_id) ?? null} onOpen={() => navigate(`/producao/obras/${j.id}`)} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">Sem obras.</p>
                )}
                {overVans.map((v) => (
                  <p key={v.id} className="mt-2 text-xs font-medium text-amber-800">
                    {v.name}: {js.filter((j) => j.van_id === v.id).length} obras neste dia
                  </p>
                ))}
              </section>
            )
          })}
        </div>

        <div className="hidden overflow-x-auto rounded-xl border border-border md:block">
          <table className="w-full min-w-[900px] table-fixed border-collapse text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="w-44 border-b px-3 py-2 text-left text-xs font-medium text-muted-foreground border-border">Carrinha</th>
                {days.map((d) => (
                  <th key={d} className={cn("border-b border-l px-2 py-2 text-left text-xs font-medium border-border", d === today && "bg-accent/10 text-accent")}>
                    {fmtDay(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ van }) => {
                const chefe = board.technicians.find((t) => t.id === van?.technician_id)
                return (
                  <tr key={van?.id ?? "_none"} className="align-top">
                    <th scope="row" className="border-b px-3 py-2 text-left font-normal border-border">
                      {van ? <VanPill van={van} className="text-sm" /> : <span className="text-sm font-medium text-muted-foreground">Sem carrinha</span>}
                      {van && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {[van.odoo_location, chefe ? `Chefe ${chefe.name}` : "Sem chefe fixo"].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </th>
                    {days.map((d) => {
                      const js = cellJobs(van?.id ?? null, d)
                      const over = van && js.length >= threshold
                      const escalated = [...new Map(js.flatMap((j) => teamOf(j, board.technicians)).map((t) => [t.id, t])).values()]
                      return (
                        <td key={d} className={cn("border-b border-l p-1.5 border-border", d === today && "bg-accent/5", over && "bg-amber-50")}>
                          <div className="flex min-h-[72px] flex-col gap-1">
                            {js.map((j) => (
                              <WeekCard key={j.id} job={j} day={d} onOpen={() => navigate(`/producao/obras/${j.id}`)} />
                            ))}
                            {escalated.length > 0 && (
                              <div className="mt-auto flex flex-wrap gap-1 pt-1">
                                {escalated.map((t) => {
                                  const absent = isAbsent(t.id, d, board.absences)
                                  return (
                                    <span
                                      key={t.id}
                                      title={absent ? `${t.name} — ausente (${absent.reason ?? ""})` : t.name}
                                      className={cn(
                                        "rounded px-1 text-[10px] font-semibold",
                                        absent ? "bg-red-100 text-red-700 line-through opacity-70" : "bg-muted text-muted-foreground",
                                      )}
                                    >
                                      {initialsOf(t)}
                                    </span>
                                  )
                                })}
                              </div>
                            )}
                            {over && <span className="text-[10px] font-medium text-amber-800">{js.length} obras neste dia</span>}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <aside className="w-full shrink-0 space-y-4 lg:w-64">
        <section className="rounded-xl border p-3 border-border">
          <h2 className="mb-2 flex items-center justify-between text-sm font-semibold">
            Por atribuir <span className="text-xs font-normal text-muted-foreground">{porAtribuir.length}</span>
          </h2>
          <ul className="space-y-2">
            {porAtribuir.map((j) => (
              <li key={j.id} className="rounded-lg border bg-card p-2 border-border">
                <div className="truncate text-sm font-medium">{j.client_name}</div>
                <div className="truncate text-xs text-muted-foreground">{[j.city, kindText(j)].filter(Boolean).join(" · ")}</div>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <MaterialBadge job={j} compact />
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openAssign({ job: j })}>
                    <Plus className="h-3.5 w-3.5" /> Atribuir
                  </Button>
                </div>
              </li>
            ))}
            {porAtribuir.length === 0 && <li className="text-xs text-muted-foreground">Nada por atribuir.</li>}
          </ul>
        </section>
        <section className="rounded-xl border p-3 border-border">
          <h2 className="mb-2 text-sm font-semibold">Ausências nesta semana</h2>
          <ul className="space-y-1 text-xs">
            {weekAbsences.map((a) => (
              <li key={a.id}>
                <span className="font-medium">{board.technicians.find((t) => t.id === a.technician_id)?.name ?? "—"}</span>
                <span className="text-muted-foreground"> · {a.reason ?? "Ausente"} · {fmtShort(a.date_from)}–{fmtShort(a.date_to)}</span>
              </li>
            ))}
            {weekAbsences.length === 0 && <li className="text-muted-foreground">Ninguém ausente.</li>}
          </ul>
        </section>
      </aside>
    </div>
  )
}

function WeekCard({ job, day, van, onOpen }: { job: Job; day: string; van?: Van | null; onOpen(): void }) {
  const { board } = useProducao()
  // Modo `host`: parada é a paragem do contrato do Koortex.
  const hostMode = board?.config.contracts_backend === "host"
  const held = isHeld(job, hostMode)
  const days = jobDays(job)
  const idx = days.indexOf(day)
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "w-full rounded-md border bg-card px-1.5 py-1 text-left shadow-sm hover:shadow border-border",
        held && "border-red-200 bg-red-50",
        job.status === "concluida" && "opacity-60",
      )}
    >
      <div className="truncate text-xs font-semibold">{job.client_name}</div>
      {held && (
        <div className="truncate text-[10px] text-red-700" title={holdReasonOf(job, hostMode) ?? undefined}>
          {hostMode ? "Parada pelo gestor" : "Parada"}: {holdReasonOf(job, hostMode) ?? "sem motivo"}
        </div>
      )}
      <div className="flex items-center justify-between gap-1">
        <span className="truncate text-[10px] text-muted-foreground">
          {days.length > 1 ? `dia ${idx + 1}/${days.length}` : job.city ?? ""}
        </span>
        {idx === 0 && <MaterialBadge job={job} compact />}
      </div>
      {van !== undefined && (
        <div className="mt-1">{van ? <VanPill van={van} /> : <span className="text-[10px] text-muted-foreground">Sem carrinha</span>}</div>
      )}
    </button>
  )
}
